import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Textarea from 'react-textarea-autosize';
import isEqual from 'lodash/isEqual';

import EntryDialog from 'components/Portfolio/EntryDialog/EntryDialog';
import {
  CharCount,
  Checkbox,
  Field,
  MonthYearField,
  inputClass,
} from 'components/Portfolio/EntryDialog/EntryFields';
import fieldStyles from 'components/Portfolio/EntryDialog/EntryDialog.module.scss';
import { fromMonthYear, isAfter, toMonthYear } from 'helpers/careerEntries';
import { getErrorMessage } from 'helpers/apiErrors';

const MAX_DESCRIPTION = 3000;

const toDraft = (role) => ({
  jobTitle: role?.job_title || '',
  company: role?.company_name || '',
  start: toMonthYear(role?.start_date),
  end: toMonthYear(role?.end_date),
  current: !!role && !role.end_date,
  description: role?.description || '',
});

const isComplete = ({ month, year }) => month !== '' && year !== '';

const RoleDialog = ({ show, role, saving, onSave, onDelete, onClose }) => {
  const [initial] = useState(() => toDraft(role));
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState('');
  const isEdit = !!role;

  const set = (changes) => setDraft((current) => ({ ...current, ...changes }));

  const endBeforeStart =
    !draft.current && isAfter(draft.start, draft.end)
      ? 'The end date can’t be before the start date.'
      : '';
  const dirty = !isEqual(draft, initial);
  const canSave =
    dirty &&
    !!draft.jobTitle.trim() &&
    !!draft.company.trim() &&
    isComplete(draft.start) &&
    (draft.current || isComplete(draft.end)) &&
    !endBeforeStart;

  const handleSave = () => {
    setError('');
    onSave({
      ...role,
      job_title: draft.jobTitle.trim(),
      company_name: draft.company.trim(),
      description: draft.description,
      start_date: fromMonthYear(draft.start, role?.start_date),
      end_date: draft.current ? '' : fromMonthYear(draft.end, role?.end_date),
    }).then(onClose, (apiError) => setError(getErrorMessage(apiError)));
  };

  const roleName = [role?.job_title, role?.company_name]
    .filter(Boolean)
    .join(' at ');

  return (
    <EntryDialog
      show={show}
      title={isEdit ? 'Edit role' : 'Add a role'}
      subtitle="Shown under Work experience on your portfolio."
      dirty={dirty}
      canSave={canSave}
      saving={saving}
      saveLabel={isEdit ? 'Save changes' : 'Add role'}
      error={error}
      onSave={handleSave}
      onClose={onClose}
      onDelete={isEdit ? onDelete : null}
      deleteLabel="Delete role"
      deletePrompt={`Delete ${
        roleName || 'this role'
      }? You can undo straight after.`}
    >
      <Field id="role-title" label="Job title">
        <input
          id="role-title"
          type="text"
          className={inputClass()}
          value={draft.jobTitle}
          onChange={(event) => set({ jobTitle: event.target.value })}
          placeholder="For example, Product Designer"
          maxLength={255}
        />
      </Field>
      <Field id="role-company" label="Company or client">
        <input
          id="role-company"
          type="text"
          className={inputClass()}
          value={draft.company}
          onChange={(event) => set({ company: event.target.value })}
          placeholder="For example, Google or Self-employed"
          maxLength={255}
        />
      </Field>
      <div className={fieldStyles.row}>
        <MonthYearField
          id="role-start"
          legend="Started"
          value={draft.start}
          onChange={(start) => set({ start })}
        />
        <MonthYearField
          id="role-end"
          legend="Ended"
          value={draft.end}
          onChange={(end) => set({ end })}
          error={endBeforeStart}
          disabled={draft.current}
          disabledText="Present"
        />
      </div>
      <Checkbox
        id="role-current"
        checked={draft.current}
        onChange={(current) => set({ current })}
      >
        I currently work here
      </Checkbox>
      <Field
        id="role-description"
        label="Description"
        optional
        help="The first two lines show on your portfolio."
        aside={<CharCount value={draft.description} max={MAX_DESCRIPTION} />}
      >
        <Textarea
          id="role-description"
          className={inputClass()}
          value={draft.description}
          onChange={(event) => set({ description: event.target.value })}
          placeholder="What you did and what changed because of it. Two or three lines is plenty."
          minRows={4}
          maxRows={12}
          maxLength={MAX_DESCRIPTION}
        />
      </Field>
    </EntryDialog>
  );
};

RoleDialog.propTypes = {
  show: PropTypes.bool,
  role: PropTypes.object,
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

export default RoleDialog;
